<template>
  <div v-if="mapLoadError" class="map-frame">
    <div class="map-error-box">
      <div class="error-text">
        <span>지도를 불러오지 못했어요</span>
      </div>

      <v-btn class="btn-icon btn-outline" @click="retryLoadMap">
        <v-icon>mdi-cached</v-icon>
      </v-btn>
    </div>
  </div>

  <div v-else class="map-frame">
    <div v-show="posStatus !== 'success'" class="info-txt-box">
      <div class="status" :class="`info-${posStatus}`">
        <v-icon>
          {{ getLocationInfoIcon }}
        </v-icon>
        <span>
          {{ getLocationInfoTxt }}
        </span>
      </div>
    </div>

    <div class="map-wrap">
      <div ref="mapEl" class="map"></div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  //   props: { currLocation: Object },

  data: () => ({
    scriptId: 'naver-map-sdk',
    mapLoadError: false,
    posStatus: 'loading', // 위치 상태 (success / warning / loading)

    posTimer: null,
    lastPanTs: 0,

    map: null,
    marker: null,
    zoom: 17, // 기본 줌 레벨
  }),

  computed: {
    ...mapGetters(['GET_CURRENT_LOCATION']),

    hasCurrentLocation() {
      return (
        this.GET_CURRENT_LOCATION &&
        typeof this.GET_CURRENT_LOCATION.lat === 'number' &&
        typeof this.GET_CURRENT_LOCATION.lon === 'number'
      );
    },

    getLocationInfoIcon() {
      if (this.hasCurrentLocation) {
        return '';
      } else if (this.posStatus === 'warning') {
        return 'mdi-map-marker-alert';
      } else {
        return 'mdi-dots-horizontal-circle';
      }
    },

    getLocationInfoTxt() {
      if (this.hasCurrentLocation) {
        return '';
        // return `현재 위치: (lat: ${this.GET_CURRENT_LOCATION.lat.toFixed(
        //   5,
        // )}, lon: ${this.GET_CURRENT_LOCATION.lon.toFixed(5)})`;
      } else if (this.posStatus === 'warning') {
        return '현재 위치를 찾을 수 없습니다.'; // 앱에서 전달받는 실시간 위치값이 없을 때
      } else {
        return '현재 위치 정보를 불러오는 중입니다...';
      }
    },
  },

  mounted() {
    this.loadNaverMapSdk()
      .then(() => {
        this.initMap();
        this.startLocationSync();
      })
      .catch(err => {
        alert(
          '지도를 불러오지 못했어요. 네트워크 상태를 확인 후 다시 시도해주세요 \n err :: ',
          err,
        );
        this.mapLoadError = true;
      });
  },

  methods: {
    /**
     * 네이버 지도 SDK 동적 로드 함수
      - SDK가 이미 로드되어 있다면 즉시 resolve
      - 중복 로드 방지 위해 기존 스크립트 요소 확인
      - 로드 이벤트 핸들러에서 naver.maps 객체 존재 여부 추가 확인
     */
    loadNaverMapSdk() {
      // * 이미 로드된 경우 즉시 반환
      if (window.naver && window.naver.maps) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        // 1. dom에서 기존 스크립트 요소 확인(방어용)
        const existing = document.getElementById(this.scriptId);

        if (existing) {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () => {
            // ** 거의 발생하지 않는 케이스지만 방어용
            reject(new Error('[!] Failed to load Naver Maps SDK script'));
          });

          // 1-2. 기존 스크립트가 있지만 naver.maps가 아직 안 잡히는 경우 대비
          const check = setInterval(() => {
            if (window.naver && window.naver.maps) {
              clearInterval(check);
              resolve();
            }
          }, 50);

          setTimeout(() => {
            clearInterval(check);
            reject(new Error('[!] SDK ready timeout (naver.maps not found)'));
          }, 8000);

          return;
        }

        // 2. SDK 스크립트 동적 생성 및 로드
        const script = document.createElement('script');
        script.id = this.scriptId;
        const clientId = process.env.VUE_APP_NAVER_MAP_CLIENT_ID; // 🗝️ 네이버 클라우드 플랫폼에서 발급받은 clientId
        script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}`;
        script.async = true;
        script.defer = true;

        // 3. 로드 이벤트 핸들러 설정
        script.onload = () => {
          // ** onload 직후 naver가 아직 안 잡히는 경우 대비
          const check = setInterval(() => {
            if (window.naver && window.naver.maps) {
              clearInterval(check); // SDK 완전히 로드됨
              resolve();
            }
          }, 50);
          setTimeout(() => {
            clearInterval(check);
            reject(new Error('[!] SDK ready timeout (naver.maps not found)'));
          }, 8000);
        };

        // 4. 에러 핸들러 설정
        script.onerror = () =>
          reject(new Error('[!] Failed to load Naver Maps SDK scPript'));

        // 5. 문서에 스크립트 요소 추가하여 로드 시작
        document.head.appendChild(script);
      });
    },

    /**
     * 네이버 지도 초기화 함수
      - SDK가 로드된 후 호출되어야 함
      - 지도와 마커를 생성하여 지정된 위치에 표시
     */
    initMap() {
      const { naver } = window;

      if (!this.$refs.mapEl) {
        // alert('지도 요소를 찾을 수 없습니다.. 잠시 후 다시 시도해주세요.');
        return;
      }

      // ** 위치 상태 셋팅 (지금 값이 없더라도 들어올 수 있으니 리턴x)
      this.posStatus = this.hasCurrentLocation ? 'success' : 'warning';

      const center =
        this.posStatus === 'success'
          ? new naver.maps.LatLng(
              this.GET_CURRENT_LOCATION.lat,
              this.GET_CURRENT_LOCATION.lon,
            )
          : new naver.maps.LatLng(0, 0); // 위치 정보 없을 때 일단 비워두기..

      this.map = new naver.maps.Map(this.$refs.mapEl, {
        center,
        zoom: this.zoom,
      });
      this.marker = new naver.maps.Marker({
        position: center,
        map: this.map,
      });

      // 지도 리사이즈 트리거
      window.naver.maps.Event.trigger(this.map, 'resize');
    },

    /**
     * 사용자의 현재 위치를 주기적으로 업데이트하여 지도와 마커를 이동
     * - 1초마다 위치를 확인하여 마커 위치 업데이트
     * - 화면 이동은 3초에 한 번만 수행하여 부드러운 UX 제공
     */
    startLocationSync() {
      if (this.posTimer) return; // 중복 방지

      this.posTimer = setInterval(() => {
        // 1. 위치 정보 존재여부 확인
        if (!this.hasCurrentLocation) {
          this.posStatus = 'warning';
          return;
        }
        this.posStatus = 'success';

        // 2. 지도와 마커가 초기화되어 있는지 확인
        if (!this.map || !this.marker || !window.naver?.maps) return;

        // 3. 새로운 위치 객체 생성
        const newLatLng = new window.naver.maps.LatLng(
          this.GET_CURRENT_LOCATION.lat,
          this.GET_CURRENT_LOCATION.lon,
        );

        // 4. 마커 위치 업데이트 및 지도 중심 이동
        this.marker.setPosition(newLatLng);
        // this.map.setCenter(newLatLng);

        // 5. 화면 이동은 3초에 한 번만 수행하여 부드러운 UX 제공
        const now = Date.now();
        if (now - this.lastPanTs > 3000) {
          this.lastPanTs = now;
          this.map.panTo(newLatLng);
        }
      }, 1000); // ** 1초마다 위치 업데이트
    },

    /**
     * 위치 동기화 중지
     */
    stopLocationSync() {
      if (this.posTimer) {
        clearInterval(this.posTimer); // 타이머 정리 (메모리 누수 방지)
        this.posTimer = null;
      }
    },

    /**
     * 맵 정리
     */
    cleanupMap() {
      // if (this.$refs.mapEl) this.$refs.mapEl.innerHTML = ''; // mapEl Dom 정리
      this.map = null;
      this.marker = null;
    },

    /**
     * 지도 로드 재시도
     */
    retryLoadMap() {
      this.mapLoadError = false;
      this.loadNaverMapSdk()
        .then(() => {
          if (!this.map) {
            this.initMap();
          }
          this.startLocationSync();
        })
        .catch(err => {
          alert(
            '지도를 불러오지 못했어요. 네트워크 상태를 확인 후 다시 시도해주세요 \n err :: ',
            err,
          );
          this.mapLoadError = true;
        });
    },
  },

  beforeDestroy() {
    this.cleanupMap();
    this.stopLocationSync();
  },
};
</script>

<style scoped lang="scss">
// _locationCheckDialog.scss
</style>
